import type { ReactNode } from 'react'

import { FlowPage, PrimaryButton } from '@monthio/design-system'
import { AssetOverviewIntroIllustration } from '@monthio/icons'
import {
	eventHubService,
	flowAnimationService,
	FlowContainer,
	LoadingScreen,
	useModal,
} from '@monthio/react-components'
import { AddButton } from '#modules/DebtOverview/Components/addButton'
import { useFormik } from 'formik'
import React, {
	Fragment,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import type {
	Asset,
	OverarchingAssetType,
	RealEstate,
	Vehicle,
} from './AssetOverview.types'

import { budgetApiClient } from '../../api/budgetApiClient'
import { flowContainerWithFixedButtonsDirectParentCssHack } from '../../modulesTemp/shared/slider'
import {
	AssetType,
	groupByOverarchingAssetType,
	overarchingTypeOf,
	overarchingTypeOrder,
	RealEstateFinancingType,
} from './AssetOverview.types'
import { AddAsset } from './Components/AddOrEditAsset/AddAsset'
import { ConfirmAddDuplicate } from './Components/AddOrEditAsset/ConfirmAddDuplicate'
import { nameof } from './Components/AddOrEditAsset/FieldModels'
import { AssetCategoryList } from './Components/AssetCategoryList'
import { AssetEmptyState } from './Components/AssetEmptyState'
import { AssetOverviewIntro } from './Components/AssetOverviewIntro'
import { LoadingCarousel } from './Components/LoadingCarousel'
import { MissingAssetsPrompt } from './Components/MissingAssetsPrompt'
import { useSteps } from './Components/useSteps'
import { useAssetOverviewModule } from './useAssetOverviewModule'

type AssetFlowState =
	| 'init'
	| 'user-input'
	| 'submitting'
	| 'submitted'
	| 'error'

const Form = styled.form`
	min-height: 100%;

	input {
		font-size: 22px;
	}

	${flowContainerWithFixedButtonsDirectParentCssHack}
`

const useAddButtonText = (): ((
	overarchingType: OverarchingAssetType,
) => string) => {
	const { t } = useTranslation()
	return (overarchingType: OverarchingAssetType): string => {
		switch (overarchingType) {
			case 'realEstate':
				return t('asset-overview.button.add-asset-real-estate')
			case 'vehicle':
				return t('asset-overview.button.add-asset-vehicle')
		}
	}
}

type AssetFlowStep = 'start' | OverarchingAssetType

const OverviewIconImg = styled(AssetOverviewIntroIllustration)`
	width: 100%;
	max-width: 500px;
`

const useFlowContainerProps = (
	step: AssetFlowStep,
	assets: Asset[],
): {
	title: string
	subtitle?: string
	icon?: React.ReactNode
} => {
	const { t } = useTranslation()
	switch (step) {
		case 'start':
			return {
				title: t('asset-overview.into-page.title'),
				subtitle: t('asset-overview.into-page.subtitle'),
				icon: <OverviewIconImg />,
			}

		case 'realEstate': {
			const isEmpty =
				assets.filter((x) => overarchingTypeOf(x.assetType) === 'realEstate')
					.length === 0
			return {
				title: isEmpty
					? t('asset-overview.realEstate-page.empty-title')
					: t('asset-overview.realEstate-page.title'),
				subtitle: isEmpty
					? t('asset-overview.realEstate-page.empty-subtitle')
					: t('asset-overview.realEstate-page.subtitle'),
			}
		}

		case 'vehicle': {
			const isEmpty =
				assets.filter((x) => overarchingTypeOf(x.assetType) === 'vehicle')
					.length === 0
			return {
				title: isEmpty
					? t('asset-overview.vehicle-page.empty-title')
					: t('asset-overview.vehicle-page.title'),
				subtitle: isEmpty
					? t('asset-overview.vehicle-page.empty-subtitle')
					: t('asset-overview.vehicle-page.subtitle'),
			}
		}
	}
}

function splitAddress(address: string): string[] {
	return address.split(/(\s+|,)/g).filter((x) => x.trim() !== '' && x !== ',')
}

const areSimilar = (a1: string, a2: string): boolean => {
	const a1Parts = splitAddress(a1)
	const a2Parts = splitAddress(a2)
	return (
		a1Parts.length === a2Parts.length &&
		a1Parts.every((part) => a2Parts.includes(part))
	)
}

export const AssetOverview: React.FC = () => {
	const { openModal, closeModal } = useModal()
	const [isOverarchingType, setIsAddingOverarchingType] =
		useState<OverarchingAssetType | null>(null)
	const [submitted, setSubmitted] = useState<AssetFlowState>('init')

	const { config, finish } = useAssetOverviewModule()

	const documentation = useRef<Map<string | number, File[]>>(
		new Map<string | number, File[]>(),
	)

	const { t } = useTranslation()

	const getAddButtonText = useAddButtonText()

	const hasVehicle = (assets: Asset[]) =>
		assets.some((asset) => overarchingTypeOf(asset.assetType) === 'vehicle')

	const createAssets = async (assets: Asset[]) => {
		const addedByApplicantAssets = assets.filter(
			(asset) => asset.addedByApplicant,
		)

		const vehicleAssetsToLookup = addedByApplicantAssets
			.filter((asset) =>
				['vehicle'].includes(overarchingTypeOf(asset.assetType)),
			)
			.map((asset) => asset as Vehicle)
			.filter((vehicle) => vehicle.vehicleIdentificationNumber !== undefined)

		const realEstateAssetsToLookup = addedByApplicantAssets
			.filter((asset) =>
				['realEstate'].includes(overarchingTypeOf(asset.assetType)),
			)
			.map((asset) => asset as RealEstate)
			.filter((realEstate) => realEstate.bfeNumber !== undefined)

		let numberOfAssetsToLookup =
			vehicleAssetsToLookup.length + realEstateAssetsToLookup.length
		const timeoutId = setTimeout(() => {
			goToTheNextModule()
			console.log(
				'finish assetOverview with timeout, numberOfAssetsToLookup:',
				numberOfAssetsToLookup,
			)
		}, 10000)
		const vehicleEventUnsubscribe = eventHubService.subscribe<{
			applicantSessionId: string
		}>('AssetsVehicleFound', () => {
			numberOfAssetsToLookup--
			if (numberOfAssetsToLookup !== 0) return
			vehicleEventUnsubscribe()
			realEstateEventUnsubscribe()
			clearTimeout(timeoutId)
			console.log('finish assetOverview, AssetsVehicleFound')
			goToTheNextModule()
		})

		const realEstateEventUnsubscribe = eventHubService.subscribe<{
			applicantSessionId: string
		}>('AssetsHomeFound', () => {
			numberOfAssetsToLookup--
			if (numberOfAssetsToLookup !== 0) return
			vehicleEventUnsubscribe()
			realEstateEventUnsubscribe()
			clearTimeout(timeoutId)
			console.log('finish assetOverview, AssetsHomeFound')
			goToTheNextModule()
		})

		await budgetApiClient.createAssets(assets)

		if (numberOfAssetsToLookup == 0) {
			clearTimeout(timeoutId)
			vehicleEventUnsubscribe()
			realEstateEventUnsubscribe()
			console.log('finish assetOverview, numberOfAssetsToLookup == 0')
			goToTheNextModule()
		}
	}

	const goToTheNextModule = useCallback(() => {
		finish()
	}, [finish])

	const openMissingAssetsModal = (errorMessage: string, assets: Asset[]) => {
		openModal({
			id: 'missingAssetsPrompt',
			title: t('asset-overview-missingAssetsPrompt-title'),
			responsive: false,
			closable: false,
			bodyRender: () => (
				<MissingAssetsPrompt
					error={errorMessage}
					handleAddAssets={(assetType: OverarchingAssetType | undefined) =>
						handleAddMoreAssets(assetType)
					}
					handleContinue={() => handleMissingAssetsConfirmed(assets)}
				/>
			),
		})
	}

	const handleAddMoreAssets = useCallback(
		(assetType: OverarchingAssetType | undefined) => {
			setSubmitted('user-input')
			if (assetType) {
				setIsAddingOverarchingType(assetType)
			}
			closeModal('missingAssetsPrompt')
		},
		[closeModal],
	)

	const handleMissingAssetsConfirmed = useCallback(
		async (assets: Asset[]) => {
			await createAssets(assets)
			closeModal('missingAssetsPrompt')
		},
		[closeModal],
	)

	const handleSubmit = async (assets: Asset[]) => {
		setSubmitted('submitting')

		if (hasVehicle(assets)) {
			await createAssets(assets)
			return
		}

		try {
			const validationResult = await budgetApiClient.validateAssets(assets)
			if (validationResult.isValid) {
				await createAssets(assets)
				return
			}

			openMissingAssetsModal(validationResult.error!.message, assets)
		} catch (error: any) {
			console.error(
				'[AssetOverviewPage.handleSubmit] Validation threw error:',
				error,
			)
			setSubmitted('error')
		}
	}

	const {
		submitForm,
		values: { assets },
		setFieldValue,
	} = useFormik<{ assets: Asset[] }>({
		onSubmit: async ({ assets }) => handleSubmit(assets),
		initialValues: { assets: [] },
	})

	useEffect(() => {
		const fetchAssets = async () => {
			const hasBankModule = config.flowHasBankModule
			try {
				const data = await budgetApiClient.fetchAssets(!hasBankModule)
				await setFieldValue('assets', data)
			} finally {
				flowAnimationService.showAnimation = false
				setSubmitted('user-input')
			}
		}
		fetchAssets()
	}, [config.flowHasBankModule, setFieldValue])

	const { currentStep, setCurrentStep, goBack } =
		useSteps<AssetFlowStep>('start')

	const { title, subtitle, icon } = useFlowContainerProps(currentStep, assets)

	const deleteAsset = useCallback(
		(asset: Asset) => {
			const index = assets.findIndex((a) => a.id === asset.id)
			if (index < 0) return

			if (asset.addedByApplicant) {
				assets.splice(index, 1)
				setFieldValue('assets', Array.from(assets))
				documentation.current.delete(asset.id)
			} else {
				setFieldValue(
					`assets[${index}].${nameof<Asset>('deletedByApplicant')}`,
					true,
				)
			}
		},
		[assets, setFieldValue],
	)

	const confirmAddDuplicateModal = useModal()

	const isUnique = useCallback(
		(asset: Asset) => {
			switch (overarchingTypeOf(asset.assetType)) {
				case 'vehicle':
					return !assets.some(
						(x) =>
							overarchingTypeOf(x.assetType) === 'vehicle' &&
							(x as Vehicle).registrationNumber ===
								(asset as Vehicle).registrationNumber,
					)
				case 'realEstate':
					return !assets.some(
						(x) =>
							overarchingTypeOf(x.assetType) === 'realEstate' &&
							areSimilar(
								(x as RealEstate).address,
								(asset as RealEstate).address,
							),
					)
			}
		},
		[assets],
	)

	const addOrEditAsset = useCallback(
		async (asset: Asset) => {
			const existingIndex = assets.findIndex(({ id }) => id === asset.id)
			if (existingIndex >= 0) {
				setFieldValue(`assets[${existingIndex}]`, asset)
			} else {
				const assetType = overarchingTypeOf(asset.assetType)
				const assetTypeTranslation = t(
					`assetOverview-type-${assetType}`,
				).toLowerCase()
				if (!isUnique(asset)) {
					const confirmed = await confirmAddDuplicateModal.openModal({
						bodyRender: ({ close }) => (
							<ConfirmAddDuplicate assetType={assetType} close={close} />
						),
						title: t('asset-overview.add-asset.confirm-duplicate.title', {
							assetType: assetTypeTranslation,
						}),
					})
					if (!confirmed) {
						return
					}
				}
				setFieldValue('assets', [...assets, asset])
				setIsAddingOverarchingType(null)
			}
		},
		[assets, confirmAddDuplicateModal, isUnique, setFieldValue, t],
	)

	const assetGroups = useMemo(
		() => groupByOverarchingAssetType(assets),
		[assets],
	)

	const overviewPages: { [key: string]: ReactNode } = useMemo(
		() =>
			Object.fromEntries([
				['start', <AssetOverviewIntro key="start" />],
				...overarchingTypeOrder.map((key) => [
					key,
					<Fragment key={key}>
						{assetGroups[key].assets.length > 0 ? (
							<AssetCategoryList
								assets={assetGroups[key].assets}
								onChange={(asset: Asset) => addOrEditAsset(asset)}
								onDelete={deleteAsset}
								shouldInputCooperativeHomeValue={config.shouldInputCooperativeHomeValue}
							/>
						) : (
							<AssetEmptyState
								overarchingAssetType={key}
								assetCount={assetGroups[key].assets.length}
							/>
						)}
						<AddButton
							id={`add-asset-${key}`}
							onAdd={() => setIsAddingOverarchingType(key)}
							title={getAddButtonText(key)}
						/>
					</Fragment>,
				]),
			]),
		[addOrEditAsset, assetGroups, deleteAsset, getAddButtonText],
	)

	if (submitted === 'init') {
		return <LoadingCarousel />
	}
	if (submitted === 'submitting') {
		return (
			<FlowPage>
				<FlowPage.Content $isCentered>
					<LoadingScreen />
				</FlowPage.Content>
			</FlowPage>
		)
	}

	if (submitted === 'error') {
		return <FlowContainer title={t('assetOverviewPage-failure-text')} />
	}

	const buttons: ReactNode[] = []

	const goToNext = () => {
		if (currentStep === 'start') {
			return setCurrentStep(overarchingTypeOrder[0]!)
		}
		if (currentStep === overarchingTypeOrder.at(-1)) {
			return submitForm()
		}
		return setCurrentStep(
			overarchingTypeOrder[overarchingTypeOrder.indexOf(currentStep) + 1]!,
		)
	}

	const goToNextRealEstateDisabled =
		currentStep === 'realEstate' &&
		(assets.some(
			(x) =>
				x.deletedByApplicant === false &&
				overarchingTypeOf(x.assetType) === 'realEstate' &&
				x.financingType === RealEstateFinancingType.None,
		) ||
			(config.shouldInputCooperativeHomeValue &&
				assets.some(
					(x) =>
						x.deletedByApplicant === false &&
						overarchingTypeOf(x.assetType) === 'realEstate' &&
						(x as RealEstate).financingType ===
							RealEstateFinancingType.Cooperative &&
						(x as RealEstate).assetValueAddedByApplicant === undefined,
				)))

	const goToNextVehicleDisabled =
		currentStep === 'vehicle' &&
		assets.some(
			(x) =>
				x.deletedByApplicant === false &&
				overarchingTypeOf(x.assetType) === 'vehicle' &&
				x.assetType !== AssetType.CampingWagon &&
				(x as Vehicle).yearlyMileage === undefined,
		)

	const goToNextDisabled = goToNextRealEstateDisabled || goToNextVehicleDisabled

	if (typeof currentStep === 'string') {
		buttons.push(
			<PrimaryButton
				key="primary"
				type="button"
				disabled={goToNextDisabled}
				onClick={goToNext}
			>
				{t('asset-overview.button.next')}
			</PrimaryButton>,
		)
	}

	return (
		<Form>
			{isOverarchingType ? (
				<AddAsset
					overarchingType={isOverarchingType}
					onAdd={(asset) => addOrEditAsset(asset)}
					onCancel={() => setIsAddingOverarchingType(null)}
					shouldInputCooperativeHomeValue={config.shouldInputCooperativeHomeValue}
				/>
			) : (
				<FlowPage>
					<FlowPage.Content $spacing="xl">
						<FlowPage.Header title={title} description={subtitle} slot={icon} />
						{overviewPages[currentStep]}
					</FlowPage.Content>
					<FlowPage.Footer
						backButton={
							goBack !== undefined ? (
								<FlowPage.BackButton
									onClick={goBack}
									ariaLabel={t('btn-back')}
								/>
							) : null
						}
						actions={
							<FlowPage.Cta disabled={goToNextDisabled} onClick={goToNext}>
								{t('asset-overview.button.next')}
							</FlowPage.Cta>
						}
					/>
				</FlowPage>
			)}
		</Form>
	)
}
